import { Component, Inject, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  AdheridoControllerService,
  AdheridoEstadoControllerService,
  PageAdheridoEstadoModel
} from 'api-rest';
import { AttachedService } from '../../../services/attached.service';
import { EmpusaAuthenticationService } from '@empusa/empusa-core';
import { SnackbarService } from '../../../services/snackbar.service';
import { SpinnerService } from '../../../services/spinner.service';
import { AttachedBajaMotivoDialogComponent, TIPO_BAJA, TIPO_BAJA_ENVALORA } from '../attached-baja-motivo-dialog/attached-baja-motivo-dialog.component';

const ESTADO_BAJA_ID = 7;
const ESTADO_BAJA_ENVALORA_ID = 9;

@Component({
  selector: 'lib-attached-status-change-dialog',
  templateUrl: './attached-status-change-dialog.component.html',
  styleUrls: ['./attached-status-change-dialog.component.scss'],
})
export class AttachedStatusChangeDialogComponent implements OnInit {

  estados: any[] = [];
  adheridos:any[] = [];
  estadoActual:any;
  
  formGroup!: FormGroup;

  constructor(
    public dialogRef: MatDialogRef<AttachedStatusChangeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private adheridoSrv: AdheridoControllerService,
    private adheridoEstadoSrv: AttachedService,
    @Inject('EmpusaAuthenticationService')
    private authService: EmpusaAuthenticationService,
    @Inject('environment') private environment: any,
    private snackBarSrv: SnackbarService,
    public spinnerSrv: SpinnerService,
    public dialog: MatDialog
  ) {
    this.adheridos = this.data.adheridos;
    this.estadoActual = this.data.estadoActual;

    this.formGroup = this.formBuilder.group({
      estado: [
        { value: null, disabled: false },
        { validators: Validators.compose([Validators.required]) }
      ]
    });
  }

  ngOnInit(): void {
    this.cargarEstados();
  
    this.cargarEstadoActual();
  }

   cargarEstados(): void {
    this.adheridoEstadoSrv.getEstadosAdheridos().subscribe({
      next: (data: any) => {
        this.estados = data!.content;
      },
      error: () => { },
    });
  }

  cargarEstadoActual() {
    this.formGroup.get('estado')?.setValue(this.estadoActual);
  }

  guardar() {
    this.formGroup.markAllAsTouched();
    this.formGroup.updateValueAndValidity();

    if (this.formGroup.valid) {
      const estadoId = this.formGroup.get('estado')?.value;

      if (estadoId === ESTADO_BAJA_ID) {
        this.abrirDialogoMotivoBaja(TIPO_BAJA);
      } else if (estadoId === ESTADO_BAJA_ENVALORA_ID) {
        this.abrirDialogoMotivoBaja(TIPO_BAJA_ENVALORA);
      } else {
        this.actualizarEstadoAdheridos(estadoId);
      }

    } else {
      this.snackBarSrv.showSnackBar(
        'Debe completar los campos obligatorios',
        'error'
      );
    }
  }

  private abrirDialogoMotivoBaja(tipoBaja: string): void {
    const dialogRef = this.dialog.open(AttachedBajaMotivoDialogComponent, {
      width: '600px',
      maxWidth: '95%',
      maxHeight: '95vh',
      data: {
        adheridos: this.adheridos,
        tipoBaja: tipoBaja,
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (tipoBaja === TIPO_BAJA) {
          this.darBajaAdheridos(result);
        } else {
          this.actualizarEstadoAdheridos(ESTADO_BAJA_ENVALORA_ID, result);
        }
      }
    });
  }

  private darBajaAdheridos(datosBaja: any): void {
    const adheridosId = this.adheridos.map(x => x.id);
    this.spinnerSrv.loadingShow();
    forkJoin(adheridosId.map((id: any) => this.adheridoEstadoSrv.bajaAdherido(id, datosBaja)))
      .subscribe({
        next: () => {
          this.spinnerSrv.loadingHide();
          this.dialogRef.close(true);
          this.snackBarSrv.showSnackBar(
            'Se ha cambiado el estado de los adheridos seleccionados.',
            'success'
          );
        },
        error: (err: { message: any }) => {
          this.spinnerSrv.loadingHide();
          this.snackBarSrv.showSnackBar(
            'Ha ocurrido un error al cambiar el estado de los adheridos',
            'error'
          );
        },
      });
  }

  private actualizarEstadoAdheridos(estadoId: number, datosBaja?: any): void {
    const adheridosId = this.adheridos.map(x => x.id);
    this.spinnerSrv.loadingShow();
    this.adheridoSrv
      .multipleActualizarEstado(estadoId, { ...datosBaja, adheridosIds: adheridosId })
      .subscribe({
        next: (value: any) => {
          this.spinnerSrv.loadingHide();
          this.dialogRef.close(true);
          let mensaje = 'Se ha cambiado el estado de los adheridos seleccionados.';
          if(value && value.length > 0){
            mensaje = mensaje + '\n' + value.join('\n');
          }
          this.snackBarSrv.showSnackBar(
            mensaje,
            'success'
          );
        },
        error: (err: any) => {
          this.spinnerSrv.loadingHide();
          if (err?.status === 409 && err?.error?.detail) {
            this.snackBarSrv.showInfoModal(err.error.detail);
            return;
          }
          this.snackBarSrv.showSnackBar(
            'Ha ocurrido un error al cambiar el estado de los adheridos',
            'error'
          );
        },
      });
  }

  cerrar(): void {
    this.dialogRef.close(null);
  }

  controlHasError(controlName: string, errorName: string) {
    return (
      this.formGroup.controls[controlName].hasError(errorName) &&
      this.formGroup.controls[controlName].touched
    );
  }

  controlHasErrorSelect(controlName: string) {
    if (
      (this.formGroup.controls[controlName]?.touched ||
        this.formGroup.controls[controlName]?.dirty) &&
      this.formGroup.controls[controlName]?.errors
    ) {
      return true;
    } else {
      return false;
    }
  }

}
